import { useMemo } from 'react';
import { Localization } from '@material-table/core';
import { merge } from 'lodash';
import { type TFunction } from 'i18next';
import { useTranslations } from '../useTranslation';
import useCachedValue from '../useCachedValue';

export interface IUseTableLocalizationProps {
  searchQuery?: string | null;
  isFilterActive?: boolean;
  noDataMessageKey?: string;
  isLoading?: boolean;
  customLocalization?: Localization;
}

export interface IUseTableLocalizationReturn {
  localization: Localization;
}

interface IGetEmptyDataSourceMessageProps {
  t: TFunction<'translation'>;
  searchQuery: IUseTableLocalizationProps['searchQuery'];
  isFilterActive: IUseTableLocalizationProps['isFilterActive'];
  noDataMessageKey?: IUseTableLocalizationProps['noDataMessageKey'];
}

type TGetEmptyDataSourceMessageFunction = (
  props: IGetEmptyDataSourceMessageProps
) => string | undefined;

const getEmptyDataSourceMessage: TGetEmptyDataSourceMessageFunction = ({
  t,
  searchQuery,
  isFilterActive,
  noDataMessageKey,
}) => {
  const isSearchActive = !!searchQuery;
  const message = {
    'true-true': 'table.noData.filterAndSearchAreActive',
    'true-false': 'table.noData.searchActive',
    'false-true': 'table.noData.filterActive',
    'false-false': noDataMessageKey,
  }[`${isSearchActive!}-${isFilterActive!}`];
  if (message) {
    return t(message, { searchQuery });
  }
  return undefined;
};

const useTableLocalization = (props: IUseTableLocalizationProps) : IUseTableLocalizationReturn => {
  const { t } = useTranslations();
  const {
    searchQuery = '',
    isFilterActive = false,
    noDataMessageKey,
    isLoading = false,
    customLocalization = null,
  } = props ?? {};

  const cachedCustomLocalization = useCachedValue<Localization | null>(customLocalization);

  const localization = useMemo(() => merge(
    {
      body: {
        emptyDataSourceMessage: isLoading ? undefined : getEmptyDataSourceMessage({
          t, searchQuery, isFilterActive, noDataMessageKey,
        }),
      },
    },
    cachedCustomLocalization,
  ), [
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
    cachedCustomLocalization,
    t,
  ]);

  return { localization };
};

export default useTableLocalization;
